import { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePeriod } from '@/pages/reports/PeriodContext';

type ExportFormat = 'csv' | 'pdf' | 'google-sheets';

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExportDialog({ open, onOpenChange }: ExportDialogProps) {
  const { user } = useAuth();
  const { period } = usePeriod();
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [loading, setLoading] = useState(false);

  async function handleExport() {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('export-report', {
        body: {
          userId: user?.id,
          format,
          dateFrom: period.dateFrom,
          dateTo: period.dateTo,
        },
      });

      if (error) throw error;

      if (format === 'google-sheets') {
        window.open(data.url, '_blank');
      } else {
        const mimeType = format === 'csv' ? 'text/csv' : 'application/pdf';
        const blob = new Blob([data], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report-${period.dateFrom}-${period.dateTo}.${format}`;
        a.click();
        URL.revokeObjectURL(url);
      }

      onOpenChange(false);
    } catch (err: any) {
      toast.error(err?.message ?? 'Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export Report</DialogTitle>
        </DialogHeader>

        <RadioGroup
          value={format}
          onValueChange={(v) => setFormat(v as ExportFormat)}
          className="space-y-3"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="csv" id="format-csv" />
            <Label htmlFor="format-csv">CSV</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="pdf" id="format-pdf" />
            <Label htmlFor="format-pdf">PDF</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="google-sheets" id="format-google-sheets" />
            <Label htmlFor="format-google-sheets">Google Sheets</Label>
          </div>
        </RadioGroup>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={loading}>
            {loading ? 'Exporting...' : 'Export'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
